import {
  Box, Paper, TextField, Select, MenuItem, FormControl, InputLabel, Typography,
  Stack, Divider, Table, TableHead, TableRow, TableCell, TableBody, Button, Alert, Link,
} from '@mui/material';

// timeline 값 normalize — js-yaml 의 timestamp 자동 변환 (Date 객체) 또는 ISO
// timestamp string 을 input type="date" 가 받는 "YYYY-MM-DD" 형식으로 변환.
function dateStr(v) {
  if (v === undefined || v === null || v === '') return '';
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10);
  }
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ComponentRow from './ComponentRow';
import ContactSelector from '../team/ContactSelector';

const STATUSES = ['planned', 'in-development', 'sqa', 'training', 'released', 'on-hold'];

export default function FeatureEditor({
  feature, products, catalog, members = [], onChangeField, onChangeTimeline,
  onCompChange, onAddComp, onRemoveComp, onDelete, onSave, saving,
}) {
  // 우선순위 1: 명시적 project 매핑 (Phase A 이후 표준)
  // 우선순위 2: feature.id 가 catalog slug.toUpperCase() 와 일치 (legacy 매칭)
  const projectCharter = feature.project
    ? catalog.find((c) => c.slug === feature.project)
    : null;
  const legacyCharter = !projectCharter
    ? catalog.find((c) => c.featureId === feature.id)
    : null;
  const charterLink = projectCharter || legacyCharter;
  const isNew = !!feature._new;
  const lockId = !isNew;
  const lockName = !isNew;

  return (
    <Paper sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {feature.name || feature.id}
      </Typography>

      {charterLink ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          📁 소속 Project: <b>{charterLink.name}</b>
          {' · '}
          <Link href={charterLink.charterUrl} target="_blank" rel="noopener">차터 보기 ({charterLink.slug}.md)</Link>
          {legacyCharter && (
            <span style={{ marginLeft: 8, opacity: 0.7 }}>(legacy ID 매칭 — "소속 Project" 드롭다운에서 명시 권장)</span>
          )}
        </Typography>
      ) : feature.project ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Project <code>{feature.project}</code> 의 차터를 catalog 에서 찾지 못했습니다. 차터 markdown 이 누락되었을 수 있습니다.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          소속 Project 미지정 — 아래의 "소속 Project" 드롭다운에서 선택하면 차터와 자동 연결됩니다.
        </Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label="이름"
          value={feature.name || ''}
          onChange={(e) => onChangeField('name', e.target.value)}
          InputProps={{ readOnly: lockName }}
          helperText={lockName ? '저장된 Feature의 이름은 변경할 수 없습니다' : '입력 시 ID 자동 생성됨'}
          placeholder="예: SMARTDent Standalone License"
          fullWidth
        />
        <TextField
          label="ID"
          value={feature.id || ''}
          onChange={(e) => onChangeField('id', e.target.value)}
          InputProps={{ readOnly: lockId }}
          error={isNew && !!feature.id && !/^[A-Z0-9-]+$/.test(feature.id)}
          helperText={
            lockId
              ? '저장된 Feature의 ID는 변경할 수 없습니다'
              : (feature.id && !/^[A-Z0-9-]+$/.test(feature.id))
                ? '대문자(A-Z) · 숫자(0-9) · 하이픈(-) 만 허용'
                : '이름 입력 시 자동 생성 — 필요 시 수동 수정 가능'
          }
          sx={{ fontFamily: 'JetBrains Mono, monospace' }}
          fullWidth
        />
        <ContactSelector
          value={feature.owner || ''}
          onChange={(v) => onChangeField('owner', v || '')}
          members={members}
          label="Owner (PL)"
          size="medium"
        />
        <FormControl fullWidth>
          <InputLabel>상태</InputLabel>
          <Select label="상태" value={feature.status || 'planned'} onChange={(e) => onChangeField('status', e.target.value)}>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel shrink>소속 Project</InputLabel>
          <Select
            displayEmpty
            label="소속 Project"
            value={feature.project || ''}
            onChange={(e) => onChangeField('project', e.target.value)}
          >
            <MenuItem value=""><em>(미지정)</em></MenuItem>
            {[...catalog].sort((a, b) => a.slug.localeCompare(b.slug)).map((c) => (
              <MenuItem key={c.slug} value={c.slug}>
                {c.name} <span style={{ marginLeft: 8, opacity: 0.5, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{c.slug}</span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="설명"
          value={feature.description || ''}
          onChange={(e) => onChangeField('description', e.target.value)}
          multiline
          minRows={2}
          placeholder="Feature 설명 (선택)"
          fullWidth
        />
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h4" sx={{ mb: 1 }}>📅 Timeline</Typography>
      {/* horizontal layout — 개발 phase 순 (개발 종료 → SQA → 교육 → 배포). */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {[
          ['dev_end',   '개발 종료'],
          ['sqa_start', 'SQA 시작'],
          ['sqa_end',   'SQA 종료'],
          ['training',  '교육일'],
          ['release',   '배포일'],
        ].map(([key, label]) => (
          <TextField
            key={key}
            label={label}
            type="date"
            value={dateStr(feature.timeline?.[key])}
            onChange={(e) => onChangeTimeline(key, e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: 160 }}
          />
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">🧩 Softwares</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAddComp}>추가</Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>소프트웨어</TableCell>
            <TableCell>버전</TableCell>
            <TableCell>상태</TableCell>
            <TableCell>메모</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(feature.components || []).length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                구성 소프트웨어 없음
              </TableCell>
            </TableRow>
          ) : (
            feature.components.map((c, ci) => (
              <ComponentRow
                key={ci}
                component={c}
                ci={ci}
                products={products}
                onChange={onCompChange}
                onRemove={onRemoveComp}
              />
            ))
          )}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete}>이 Feature 삭제</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={saving}>
          {saving ? '저장 중...' : '전체 저장 → GitHub'}
        </Button>
      </Box>
    </Paper>
  );
}
